import { t } from '@ankr.com/common';
import { Chain, ChainID } from '@ankr.com/chains-list';

import { IS_PROD } from 'modules/common/constants/env';

import { ChainGroup, ChainGroupID, EndpointGroup } from '../types';

const ENABLE_NOT_STABLE_NETWORKS = IS_PROD;

const EVM_JSON_RPC_NAME = 'EVM JSON-RPC';

export const getName = (key: string, isPlural?: boolean) =>
  t(`chain-item.header.endpoint-groups.${key}`, {
    plurals: isPlural ? 1 : 0,
  });

export const getFallbackEndpointGroup = (
  endpointName: Chain['name'],
): EndpointGroup => {
  const name = t('chain-item.header.endpoint-groups.fallback', {
    chain: endpointName,
    plurals: 1,
  });

  return {
    chainName: endpointName,
    id: ChainGroupID.FALLBACK,
    name,
    pluralName: name,
    urls: [],
    urlsCount: 0,
    chains: [],
  };
};

export const tendermintRpcChains = [
  ChainID.SECRET_RPC,
  ChainID.ZETACHAIN_TENDERMINT_RPC_ATHENS_TESTNET,
  ChainID.SEI_RPC,
  ChainID.ALLORA_MAINNET_RPC,
  ChainID.ALLORA_TESTNET_RPC,
  ChainID.ZERO_G_GALILEO_TENDERMINT_RPC,
  ChainID.TAC_TENDERMINT_JSON_RPC,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC,
];

export const tendermintRestChains = [
  ChainID.SECRET_REST,
  ChainID.ZETACHAIN_TENDERMINT_REST_ATHENS_TESTNET,
  ChainID.SEI_REST,
  ChainID.ALLORA_MAINNET_REST,
  ChainID.ALLORA_TESTNET_REST,
  ChainID.ZERO_G_GALILEO_TENDERMINT_REST,
  ChainID.TAC_TENDERMINT_REST,
];

const tendermintGRPCChains = [
  ChainID.ALLORA_MAINNET_GRPC,
  ChainID.ALLORA_TESTNET_GRPC,
  ChainID.KAVA_TENDERMINT_GRPC,
  ChainID.SEI_TENDERMINT_GRPC,
  ChainID.TAC_TENDERMINT_GRPC,
];

export const kavaTendermintRpcChains = [
  ChainID.KAVA_TENDERMINT_RPC,
  ChainID.KAVA_TENDERMINT_RPC_TESTNET,
];

const kavaTendermintRestChains = [
  ChainID.KAVA_TENDERMINT_REST,
  ChainID.KAVA_TENDERMINT_REST_TESTNET,
];

export const tacTendermintRestChains = [
  ChainID.TAC_TENDERMINT_REST,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_REST,
];

export const tacTendermintRpcChains = [
  ChainID.TAC_TENDERMINT_JSON_RPC,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC,
];

let chainGroups: ChainGroup[] | undefined;

// eslint-disable-next-line max-lines-per-function
export function getChainGroups(): ChainGroup[] {
  if (!chainGroups) {
    chainGroups = [
      {
        id: ChainGroupID.STANDARD_EVM,
        name: getName('standard-evm-api'),
        pluralName: getName('standard-evm-api', true),
        chains: [
          ChainID.ABCHAIN,
          ChainID.ABCHAIN_TESTNET,

          ChainID.ARBITRUM,
          ChainID.ARBITRUM_NOVA,

          ChainID.AVALANCHE,
          ChainID.AVALANCHE_EVM,
          ChainID.AVALANCHE_FUJI,
          ChainID.AVALANCHE_FUJI_EVM,

          ChainID.ATLETA,
          ChainID.ATLETA_OLYMPIA,

          ChainID.B2,
          ChainID.B2_HABITAT_TESTNET,

          ChainID.BAHAMUT,
          ChainID.BAHAMUT_OCEAN,
          ChainID.BAHAMUT_HORIZON,

          ChainID.BASE,

          ChainID.BLAST,
          ChainID.BLAST_TESTNET_SEPOLIA,

          ChainID.BERACHAIN,
          ChainID.BERACHAIN_GUARDED_TESTNET_EVM,

          ChainID.BOTANIX,
          ChainID.BOTANIX_TESTNET,

          ChainID.BSC,
          ChainID.BSC_TESTNET_CHAPEL,

          ChainID.BTTC,
          ChainID.CELO,

          ChainID.CHILIZ,
          ChainID.CHILIZ_TESTNET,

          ChainID.CORE,

          ChainID.ELECTRONEUM,
          ChainID.ELECTRONEUM_TESTNET,

          ChainID.FANTOM,
          ChainID.FANTOM_TESTNET,

          ChainID.FLARE,
          ChainID.FLARE_EVM,
          ChainID.FLARE_COSTON_EVM,
          ChainID.FLARE_COSTON2_EVM,
          ChainID.FLARE_SONGBIRD_EVM,

          ChainID.GNOSIS,
          ChainID.GNOSIS_BEACON,
          ChainID.GNOSIS_TESTNET,
          ChainID.GNOSIS_TESTNET_BEACON,

          ChainID.IOTA_EVM,
          ChainID.IOTA_EVM_TESTNET,

          ChainID.IOTEX,
          ChainID.IOTEX_TESTNET,

          ChainID.METIS,
          ChainID.MOONBEAM,

          ChainID.OPTIMISM,

          ChainID.XLAYER,
          ChainID.XLAYER_TESTNET,

          ChainID.POLYGON,
          ChainID.POLYGON_MUMBAI,
          ChainID.POLYGON_ZKEVM,

          ChainID.PLASMA,

          ChainID.SYSCOIN,

          ChainID.HECO,
          ChainID.HECO_TESTNET,

          ChainID.KITE,
          ChainID.KINTO,
          ChainID.KAIA,
          ChainID.KAIA_TESTNET,

          ChainID.MATCHAIN,

          ChainID.REDBELLY,

          ChainID.ROBINHOOD,

          ChainID.ROLLUX,
          ChainID.ROLLUX_TESTNET,

          ...(ENABLE_NOT_STABLE_NETWORKS ? [ChainID.MANTLE] : []),

          ChainID.TAIKO,
          ChainID.TAIKO_HOODI,
          ChainID.TELOS,

          ChainID.XAI,
          ChainID.XAI_TESTNET,

          ChainID.XDC,
          ChainID.XDC_TESTNET,

          ChainID.ZETACHAIN,
          ...(ENABLE_NOT_STABLE_NETWORKS
            ? [ChainID.ZETACHAIN_EVM_ATHENS_TESTNET]
            : []),

          ChainID.ZKSYNC_ERA,

          ChainID.LINEA,

          ChainID.SCROLL,

          ChainID.STORY,
          ChainID.STORY_ODYSSEY,
          ChainID.STORY_AENEID,

          ChainID.SWELL,
          ChainID.SWELL_SEPOLIA,

          ChainID.GOAT,
          ChainID.GOAT_TESTNET,

          ChainID.CORN,
          ChainID.CORN_TESTNET,
          ChainID.SONIC,
          ChainID.SONIC_BLAZE_TESTNET,

          ChainID.SOMNIA,
          ChainID.SOMNIA_TESTNET,

          ChainID.STATUS,
          ChainID.STATUS_TESTNET,

          ChainID.WHITECHAIN,
          ChainID.WHITECHAIN_TESTNET,

          ChainID.WORLDCHAIN,

          ChainID.TAC_TESTNET_SPB_EVM,

          ChainID.ETHERLINK,

          ChainID.MONAD,
          ChainID.MONAD_TESTNET,

          ChainID.XPHERE,
          ChainID.XPHERE_TESTNET,

          ChainID.DOGEOS,
          ChainID.DOGEOS_TESTNET,

          ChainID.UNICHAIN,

          ChainID.HYPEREVM,
        ],
      },
      {
        id: ChainGroupID.EVM_JSON_RPC,
        name: EVM_JSON_RPC_NAME,
        pluralName: EVM_JSON_RPC_NAME,
        chains: [
          ChainID.ZERO_G_EVM,
          ChainID.ZERO_G_GALILEO_EVM,
          ChainID.SEI_EVM,
          ChainID.TAC_EVM,
        ],
      },
      {
        id: ChainGroupID.CARDONA,
        name: 'Cardona',
        pluralName: 'Cardona',
        chains: [ChainID.POLYGON_ZKEVM_CARDONA],
      },
      {
        id: ChainGroupID.POLYGON_TESTNET,
        name: 'Testnet',
        pluralName: 'Testnet',
        chains: [ChainID.POLYGON_ZKEVM_TESTNET],
      },
      {
        id: ChainGroupID.SCROLL_ALPHA,
        name: 'Scroll Alpha',
        pluralName: 'Scroll Alpha',
        chains: [ChainID.SCROLL_TESTNET],
      },
      {
        id: ChainGroupID.C_CHAIN,
        name: getName('c-chain'),
        pluralName: getName('c-chain', true),
        chains: [
          ChainID.AVALANCHE_C,
          ChainID.AVALANCHE_FUJI_C,
          ChainID.FLARE_C,
        ],
      },
      {
        id: ChainGroupID.P_CHAIN,
        name: getName('p-chain'),
        pluralName: getName('p-chain', true),
        chains: [
          ChainID.AVALANCHE_P,
          ChainID.AVALANCHE_FUJI_P,
          ChainID.FLARE_P,
        ],
      },
      {
        id: ChainGroupID.X_CHAIN,
        name: getName('x-chain'),
        pluralName: getName('x-chain', true),
        chains: [
          ChainID.AVALANCHE_X,
          ChainID.AVALANCHE_FUJI_X,
          ChainID.FLARE_X,
        ],
      },
      {
        id: ChainGroupID.ETH_MAINNET,
        name: getName('ethereum-endpoint'),
        pluralName: getName('ethereum-endpoint', true),
        chains: [ChainID.ETH, ChainID.ETH_BEACON],
      },
      {
        id: ChainGroupID.RINKEBY,
        name: 'Rinkeby',
        pluralName: 'Rinkeby',
        chains: [ChainID.ETH_RINKEBY],
      },
      {
        id: ChainGroupID.KOVAN,
        name: 'Kovan',
        pluralName: 'Kovan',
        chains: [ChainID.ETH_KOVAN],
      },
      {
        id: ChainGroupID.ROPSTEN,
        name: 'Ropsten',
        pluralName: 'Ropsten',
        chains: [ChainID.ETH_ROPSTEN],
      },
      {
        id: ChainGroupID.GOERLI,
        name: 'Goerli',
        pluralName: 'Goerli',
        chains: [
          ChainID.BASE_TESTNET,
          ChainID.OPTIMISM_TESTNET,
          ChainID.ZKSYNC_ERA_TESTNET,
          ChainID.ARBITRUM_TESTNET,
          ChainID.MANTLE_TESTNET,
        ],
      },
      {
        id: ChainGroupID.SEPOLIA,
        name: 'Sepolia',
        pluralName: 'Sepolia',
        chains: [
          ChainID.ETH_SEPOLIA,
          ChainID.ETH_SEPOLIA_BEACON,
          ChainID.SCROLL_SEPOLIA_TESTNET,
          ChainID.OPTIMISM_SEPOLIA,
          ChainID.BASE_SEPOLIA,
          ChainID.ZKSYNC_ERA_SEPOLIA,
          ChainID.ARBITRUM_SEPOLIA,
          ChainID.MANTLE_SEPOLIA,
          ChainID.CELO_SEPOLIA,
        ],
      },
      {
        id: ChainGroupID.BAKLAVA,
        name: 'Baklava',
        pluralName: 'Baklava',
        chains: [ChainID.CELO_BAKLAVA],
      },
      {
        id: ChainGroupID.MUMBAI,
        name: 'Mumbai',
        pluralName: 'Mumbai',
        chains: [ChainID.POLYGON_MUMBAI],
      },
      {
        id: ChainGroupID.AMOY,
        name: 'Amoy',
        pluralName: 'Amoy',
        chains: [ChainID.POLYGON_AMOY],
      },
      {
        id: ChainGroupID.HOLESKY,
        name: 'Holesky',
        pluralName: 'Holesky',
        chains: [ChainID.ETH_HOLESKY, ChainID.ETH_HOLESKY_BEACON],
      },
      {
        id: ChainGroupID.HOODI,
        name: 'Hoodi',
        pluralName: 'Hoodi',
        chains: [ChainID.ETH_HOODI, ChainID.ETH_HOODI_BEACON],
      },
      {
        id: ChainGroupID.HORIZEN,
        name: 'Horizen',
        pluralName: 'Horizen',
        chains: [
          ChainID.HORIZEN,
          ChainID.HORIZEN_EVM,
          ChainID.HORIZEN_TESTNET,
          ChainID.HORIZEN_TESTNET_EVM,
        ],
      },
      {
        id: ChainGroupID.NEAR,
        name: 'Near',
        pluralName: 'Near',
        chains: [ChainID.NEAR],
      },
      {
        id: ChainGroupID.TENET,
        name: 'Tenet',
        pluralName: 'Tenet',
        chains: [ChainID.TENET, ChainID.TENET_EVM],
      },
      {
        id: ChainGroupID.NERVOS_EVM,
        name: getName('nervos-evm'),
        pluralName: getName('nervos-evm', true),
        chains: [ChainID.NERVOS_EVM],
      },
      {
        id: ChainGroupID.NERVOS_GW,
        name: getName('nervos-gw'),
        pluralName: getName('nervos-gw', true),
        chains: [ChainID.NERVOS_GW],
      },
      {
        id: ChainGroupID.NERVOS_CKB,
        name: getName('nervos-ckb'),
        pluralName: getName('nervos-ckb', true),
        chains: [ChainID.NERVOS_CKB],
      },
      {
        id: ChainGroupID.TENDERMINT_RPC,
        name: getName('tendermint-rpc'),
        pluralName: getName('tendermint-rpc', true),
        chains: tendermintRpcChains,
      },
      {
        id: ChainGroupID.TENDERMINT_REST,
        name: getName('tendermint-rest'),
        pluralName: getName('tendermint-rest', true),
        chains: tendermintRestChains,
      },
      {
        id: ChainGroupID.TENDERMINT_GRPC,
        name: getName('tendermint-grpc'),
        pluralName: getName('tendermint-grpc'),
        chains: tendermintGRPCChains,
      },
      {
        id: ChainGroupID.COSMOS_REST,
        name: getName('cosmos-rest'),
        pluralName: getName('cosmos-rest', true),
        chains: [
          ChainID.SEI_COSMOS_REST,
          ChainID.SECRET_COSMOS_REST,
          ChainID.ZETACHAIN_COSMOS_REST_ATHENS_TESTNET,
          ChainID.ALLORA_MAINNET_COSMOS_REST,
          ChainID.ALLORA_TESTNET_COSMOS_REST,
          ChainID.TAC_COSMOS_REST,
        ],
      },
      {
        id: ChainGroupID.SOLANA,
        name: getName('solana-api'),
        pluralName: getName('solana-api', true),
        chains: [ChainID.SOLANA],
      },
      {
        id: ChainGroupID.SOLANA_DEVNET,
        name: getName('solana-api'),
        pluralName: getName('solana-api', true),
        chains: [ChainID.SOLANA_DEVNET],
      },
      {
        id: ChainGroupID.REST_API,
        name: getName('rest-api'),
        pluralName: getName('rest-api', true),
        chains: [ChainID.TRON_REST],
      },
      {
        id: ChainGroupID.JSON_RPC,
        name: getName('json-rpc'),
        pluralName: getName('json-rpc', true),
        chains: [ChainID.TRON_JSON_RPC],
      },
      {
        id: ChainGroupID.SUI_MAINNET,
        name: 'SUI Mainnet',
        pluralName: 'SUI Mainnet',
        chains: [
          ChainID.SUI,
          ChainID.SUI_GRPC,
          ChainID.SUI_GRPC_ARCHIVE,
          ChainID.SUI_GRAPHQL,
        ],
      },
      {
        id: ChainGroupID.SUI_TESTNET,
        name: 'SUI Testnet',
        pluralName: 'SUI Testnet',
        chains: [ChainID.SUI_TESTNET, ChainID.SUI_TESTNET_GRPC],
      },
      {
        id: ChainGroupID.GRPC,
        name: getName('grpc'),
        pluralName: getName('grpc', true),
        chains: [ChainID.TAC_COSMOS_GRPC_WEB],
      },
      {
        id: ChainGroupID.KAVA_EVM,
        name: EVM_JSON_RPC_NAME,
        pluralName: EVM_JSON_RPC_NAME,
        chains: [
          ChainID.KAVA_EVM,
          ...(ENABLE_NOT_STABLE_NETWORKS ? [ChainID.KAVA_EVM_TESTNET] : []),
        ],
      },
      {
        id: ChainGroupID.KAVA_COSMOS_REST,
        name: 'HTTP Rest',
        pluralName: 'HTTP Rest',
        chains: [ChainID.KAVA_COSMOS_REST, ChainID.KAVA_COSMOS_REST_TESTNET],
      },
      {
        id: ChainGroupID.KAVA_TENDERMINT_REST,
        name: 'Tendermint Rest',
        pluralName: 'Tendermint Rest',
        chains: kavaTendermintRestChains,
      },
      {
        id: ChainGroupID.KAVA_TENDERMINT_RPC,
        name: 'JSON-RPC',
        pluralName: 'JSON-RPC',
        chains: kavaTendermintRpcChains,
      },
      {
        id: ChainGroupID.STELLAR_RPC,
        name: 'Stellar RPC',
        pluralName: 'Stellar RPC',
        chains: [ChainID.STELLAR_SOROBAN, ChainID.STELLAR_TESTNET_SOROBAN],
      },
      {
        id: ChainGroupID.STELLAR_HORIZON,
        name: 'Horizon',
        pluralName: 'Horizon',
        chains: [ChainID.STELLAR_HORIZON, ChainID.STELLAR_TESTNET_HORIZON],
      },
      {
        id: ChainGroupID.FLARE_COSTON,
        name: 'Coston',
        pluralName: 'Coston',
        chains: [
          ChainID.FLARE_COSTON,
          ChainID.FLARE_COSTON_EVM,
          ChainID.FLARE_COSTON_C,
          ChainID.FLARE_COSTON_P,
          ChainID.FLARE_COSTON_X,
        ],
      },
      {
        id: ChainGroupID.FLARE_COSTON2,
        name: 'Coston 2',
        pluralName: 'Coston 2',
        chains: [
          ChainID.FLARE_COSTON2,
          ChainID.FLARE_COSTON2_EVM,
          ChainID.FLARE_COSTON2_C,
          ChainID.FLARE_COSTON2_P,
          ChainID.FLARE_COSTON2_X,
        ],
      },
      {
        id: ChainGroupID.FLARE_SONGBIRD,
        name: 'Songbird',
        pluralName: 'Songbird',
        chains: [
          ChainID.FLARE_SONGBIRD,
          ChainID.FLARE_SONGBIRD_EVM,
          ChainID.FLARE_SONGBIRD_C,
          ChainID.FLARE_SONGBIRD_P,
          ChainID.FLARE_SONGBIRD_X,
        ],
      },
      {
        id: ChainGroupID.GOLDBERG,
        name: 'Goldberg',
        pluralName: 'Goldberg',
        chains: [ChainID.AVAIL_GOLDBERG_TESTNET],
      },
      {
        id: ChainGroupID.TURING,
        name: 'Turing',
        pluralName: 'Turing',
        chains: [ChainID.AVAIL_TURING_TESTNET],
      },
      {
        id: ChainGroupID.BTC,
        name: 'BTC',
        pluralName: 'BTC',
        chains: [ChainID.BTC_MAINNET, ChainID.BTC_SIGNET],
      },
      {
        id: ChainGroupID.BTC_BLOCKBOOK,
        name: 'Blockbook',
        pluralName: 'Blockbook',
        chains: [ChainID.BTC_BLOCKBOOK, ChainID.BTC_BLOCKBOOK_SIGNET],
      },
      {
        id: ChainGroupID.BEACONKIT_REST,
        name: 'Beaconkit REST',
        pluralName: 'Beaconkit REST',
        chains: [
          ChainID.ZERO_G_BEACONKIT_REST,
          ChainID.ZERO_G_GALILEO_BEACONKIT_REST,
        ],
      },
      {
        id: ChainGroupID.TON_REST,
        name: getName('rest-api'),
        pluralName: getName('rest-api', true),
        chains: [ChainID.TON_REST],
      },
      {
        id: ChainGroupID.TON_RPC,
        name: getName('json-rpc'),
        pluralName: getName('json-rpc', true),
        chains: [ChainID.TON_RPC],
      },
      {
        id: ChainGroupID.FUEL_GRAPH_QL,
        name: 'Fuel GraphQL',
        pluralName: 'Fuel GraphQL',
        chains: [ChainID.FUEL],
      },
      {
        id: ChainGroupID.MAIZENET,
        name: 'Mainnet',
        pluralName: 'Mainnet',
        chains: [ChainID.CORN_MAIZENET],
      },
      {
        id: ChainGroupID.MOVEMENT_MAINNET,
        name: 'Mainnet',
        pluralName: 'Mainnet',
        chains: [ChainID.MOVEMENT_MAINNET_REST],
      },
      {
        id: ChainGroupID.MOVEMENT_BARDOCK,
        name: 'Testnet',
        pluralName: 'Testnet',
        chains: [ChainID.MOVEMENT_BARDOCK_REST],
      },
      {
        id: ChainGroupID.TAC_SPB,
        name: 'SPB',
        pluralName: 'SPB',
        chains: [
          ChainID.TAC_TESTNET_SPB,
          ChainID.TAC_TESTNET_SPB_COSMOS_GRPC,
          ChainID.TAC_TESTNET_SPB_COSMOS_REST,
          ChainID.TAC_TESTNET_SPB_EVM,
          ChainID.TAC_TESTNET_SPB_GRPC,
          ChainID.TAC_TESTNET_SPB_TENDERMINT_REST,
          ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC,
        ],
      },
      {
        id: ChainGroupID.VFLOW,
        name: 'vFlow JSON-RPC',
        pluralName: 'vFlow JSON-RPC',
        chains: [ChainID.ZKVERIFY_VFLOW_PARACHAIN_MAINNET],
      },
      {
        id: ChainGroupID.ZKVERIFY,
        name: 'JSON-RPC',
        pluralName: 'JSON-RPC',
        chains: [ChainID.ZKVERIFY_MAINNET],
      },
      {
        id: ChainGroupID.VFLOW_PARACHAIN,
        name: 'vFlow Volta JSON-RPC',
        pluralName: 'vFlow Volta JSON-RPC',
        chains: [ChainID.ZKVERIFY_VFLOW_PARACHAIN_TESTNET],
      },
      {
        id: ChainGroupID.VOLTA,
        name: 'Volta JSON-RPC',
        pluralName: 'Volta JSON-RPC',
        chains: [ChainID.ZKVERIFY_VOLTA_TESTNET],
      },
      {
        id: ChainGroupID.SHADOWNET,
        name: 'Shadownet Testnet',
        pluralName: 'Shadownet Testnet',
        chains: [ChainID.ETHERLINK_SHADOWNET_TESTNET],
      },
      {
        id: ChainGroupID.ETHERLINK_TESTNET,
        name: 'Etherlink Testnet',
        pluralName: 'Etherlink Testnet',
        chains: [ChainID.ETHERLINK_TESTNET],
      },
      {
        id: ChainGroupID.POLKADOT,
        name: 'Polkadot',
        pluralName: 'Polkadot',
        chains: [ChainID.POLKADOT_RPC],
      },
      {
        id: ChainGroupID.POLKADOT_ASSET_HUB,
        name: 'Polkadot Asset Hub',
        pluralName: 'Polkadot Asset Hub',
        chains: [ChainID.POLKADOT_ASSET_HUB],
      },
    ];
  }

  return chainGroups;
}
